import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  Radio,
  Checkbox,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Card,
  CardContent,
  Divider,
  Container,
  Paper,
  Stack,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import PrintIcon from "@mui/icons-material/Print";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import api from "../axiosConfig";
import dayjs from "dayjs";
import { DataGridPro } from "@mui/x-data-grid-pro";
import ReactApexChart from "react-apexcharts";
import { useBranding } from "../BrandingContext";
import ReportDialog from "./ReportDialog";
import RegisterUsersDialog from './RegisterUsersDialog';
import InspectionQuestionsDialog from "./InspectionQuestionsDialog";
import RfidStatisticsDialog from "./RfidStatisticsDialog";
import "./Dashboard.css";

const EMPTY_CLASSIFICATION_STATE = {
  gas_vapor_iec: false,
  gas_vapor_atex: false,
  gas_vapor_nec500: false,
  dust_iec: false,
  dust_atex: false,
  dust_nec500: false,
};
const INSPECTION_VALIDITY_OPTIONS = [12, 24, 36, 48, 60];
const dashboardActionButtonSx = {
  minHeight: 38,
  borderRadius: 999,
  px: 1.6,
  textTransform: "none",
  fontWeight: 700,
  letterSpacing: "0.01em",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
  whiteSpace: "nowrap",
  borderColor: "rgba(148, 163, 184, 0.45)",
  backgroundColor: "rgba(255, 255, 255, 0.92)",
  transition: "background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
  "& .MuiButton-startIcon": {
    mr: 0.75,
  },
  "&:hover": {
    backgroundColor: "#ffffff",
    borderColor: "rgba(100, 116, 139, 0.55)",
    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
    transform: "translateY(-1px)",
  },
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: "2px",
  },
};

const handleDashboardCardKeyDown = (event) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    event.currentTarget.click();
  }
};

const dashboardInteractiveCardSx = {
  cursor: "pointer",
  transition: "transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease",
  "&:hover": {
    transform: "translateY(-1px)",
    borderColor: "#aebdcb",
    boxShadow: "0 7px 18px rgba(15, 23, 42, 0.09)",
  },
  "&:hover .dashboard-kpi-arrow": {
    opacity: 1,
    transform: "translateX(2px)",
  },
  "&:focus-visible": {
    outline: "2px solid var(--brand-primary, #1f6fb2)",
    outlineOffset: 2,
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:hover": {
      transform: "none",
    },
    "& .dashboard-kpi-arrow": {
      transition: "none",
    },
  },
};

const dashboardKpiCardSx = {
  height: "100%",
  borderRadius: 2,
  border: "1px solid",
  borderColor: "#d9e1e9",
  boxShadow: "0 2px 6px rgba(15, 23, 42, 0.045)",
  backgroundColor: "#ffffff",
  backgroundImage: "none",
};

const dashboardKpiTones = {
  neutral: { accent: "#64748b", soft: "#eef2f6", value: "#1f2937" },
  primary: { accent: "#2f78ae", soft: "#eaf3f8", value: "#1f2937" },
  success: { accent: "#2f855a", soft: "#eaf6ef", value: "#267047" },
  warning: { accent: "#b26a12", soft: "#fff3df", value: "#99570e" },
  error: { accent: "#bd3546", soft: "#fdecef", value: "#ad2f3f" },
  info: { accent: "#287aa5", soft: "#e8f4f8", value: "#226f95" },
};

function clampDashboardPercent(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? Math.min(100, Math.max(0, numericValue))
    : 0;
}

function formatDashboardPercent(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(1) : "0.0";
}

function DashboardKpiCard({
  label,
  value,
  secondaryValue,
  supportingText,
  tone = "neutral",
  progressValue,
  progressLabel,
  onActivate,
  ariaLabel,
}) {
  const { isBranded, brand } = useBranding();
  const cardTone = isBranded && tone === "primary"
    ? { accent: brand.primary, soft: brand.soft, value: brand.navBackground }
    : (dashboardKpiTones[tone] || dashboardKpiTones.neutral);
  const interactive = Boolean(onActivate);
  const hasProgress = Number.isFinite(Number(progressValue));
  const safeProgressValue = hasProgress
    ? clampDashboardPercent(progressValue)
    : 0;

  return (
    <Card
      onClick={onActivate}
      onKeyDown={interactive ? handleDashboardCardKeyDown : undefined}
      role={interactive ? "link" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? ariaLabel : undefined}
      sx={{
        ...dashboardKpiCardSx,
        ...(interactive ? dashboardInteractiveCardSx : {}),
      }}
    >
      <CardContent
        sx={{
          minHeight: 80,
          height: "100%",
          boxSizing: "border-box",
          p: 1.35,
          "&:last-child": { pb: 1.35 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" alignItems="center" spacing={0.8} sx={{ minWidth: 0 }}>
            <Box
              aria-hidden="true"
              sx={{
                width: 7,
                height: 7,
                flex: "0 0 auto",
                borderRadius: "50%",
                backgroundColor: cardTone.accent,
                boxShadow: `0 0 0 3px ${cardTone.soft}`,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                minWidth: 0,
                color: "text.secondary",
                fontSize: "0.69rem",
                lineHeight: 1.2,
                fontWeight: 800,
                letterSpacing: "0.055em",
              }}
            >
              {label}
            </Typography>
          </Stack>
          {interactive && (
            <Box
              className="dashboard-kpi-arrow"
              aria-hidden="true"
              sx={{
                width: 22,
                height: 22,
                flex: "0 0 auto",
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                color: cardTone.accent,
                backgroundColor: cardTone.soft,
                opacity: 0.72,
                transition: "opacity 0.16s ease, transform 0.16s ease",
              }}
            >
              <ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />
            </Box>
          )}
        </Stack>

        <Stack
          direction="row"
          alignItems="baseline"
          spacing={0.8}
          sx={{ mt: 0.55 }}
        >
          <Typography
            variant="h5"
            sx={{
              color: cardTone.value,
              fontWeight: 800,
              lineHeight: 1.05,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {value}
          </Typography>
          {secondaryValue && (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}
            >
              {secondaryValue}
            </Typography>
          )}
        </Stack>

        {supportingText && (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: "0.75rem", lineHeight: 1.35, mt: 0.55 }}
          >
            {supportingText}
          </Typography>
        )}

        {hasProgress && (
          <Box sx={{ mt: "auto", pt: 1 }}>
            <Box
              role="progressbar"
              aria-label={progressLabel}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(safeProgressValue)}
              aria-valuetext={`${safeProgressValue.toFixed(1)}%`}
              sx={{
                height: 3,
                overflow: "hidden",
                borderRadius: 999,
                backgroundColor: "#edf1f5",
              }}
            >
              <Box
                sx={{
                  width: `${safeProgressValue}%`,
                  height: "100%",
                  borderRadius: "inherit",
                  backgroundColor: cardTone.accent,
                  transition: "width 0.24s ease",
                  "@media (prefers-reduced-motion: reduce)": {
                    transition: "none",
                  },
                }}
              />
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function formatInspectionValidityLabel(months) {
  const years = Math.max(1, Math.round((months || 60) / 12));
  return `${years} year${years === 1 ? "" : "s"}`;
}

function getSelectedStandard(state, group) {
  if (group === "gas") {
    return state.gas_vapor_iec
      ? "iec"
      : state.gas_vapor_atex
      ? "atex"
      : state.gas_vapor_nec500
      ? "nec500"
      : null;
  }
  return state.dust_iec
    ? "iec"
    : state.dust_atex
    ? "atex"
    : state.dust_nec500
    ? "nec500"
    : null;
}

function normalizeClassificationState(state) {
  const gasStandard = getSelectedStandard(state, "gas");
  const dustStandard = getSelectedStandard(state, "dust");
  const normalizedDustStandard =
    gasStandard && dustStandard && gasStandard !== dustStandard
      ? null
      : dustStandard;
  return {
    gas_vapor_iec: gasStandard === "iec",
    gas_vapor_atex: gasStandard === "atex",
    gas_vapor_nec500: gasStandard === "nec500",
    dust_iec: normalizedDustStandard === "iec",
    dust_atex: normalizedDustStandard === "atex",
    dust_nec500: normalizedDustStandard === "nec500",
  };
}

function toggleGroupStandard(state, group, standard) {
  const next = { ...state };
  const fieldPrefix = group === "gas" ? "gas_vapor_" : "dust_";
  const otherGroup = group === "gas" ? "dust" : "gas";
  const otherStandard = getSelectedStandard(state, otherGroup);
  const targetField = `${fieldPrefix}${standard}`;
  const isCurrentlySelected = Boolean(state[targetField]);

  if (otherStandard && otherStandard !== standard) {
    return state;
  }

  next[`${fieldPrefix}iec`] = false;
  next[`${fieldPrefix}atex`] = false;
  next[`${fieldPrefix}nec500`] = false;

  if (!isCurrentlySelected) {
    next[targetField] = true;
  }

  return next;
}

function getCrossStandardDisabled(state, group, standard) {
  const otherGroup = group === "gas" ? "dust" : "gas";
  const otherStandard = getSelectedStandard(state, otherGroup);
  return Boolean(otherStandard && otherStandard !== standard);
}

function Dashboard() {
  const { isBranded, brand } = useBranding();
  const NAVBAR_HEIGHT_PX = 64; // adjust if your AppBar differs
  const [stats, setStats] = useState(null);
  const [typePercentages, setTypePercentages] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [expiryIsExpired, setExpiryIsExpired] = useState(false);
  const [planType, setPlanType] = useState("");
  const [currentUsers, setCurrentUsers] = useState(0);
  const [userLimit, setUserLimit] = useState(0);
  const [itemLimit, setItemLimit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [error, setError] = useState(null);
  const [me, setMe] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [dbCode, setDbCode] = useState(null);
  const [inspectionValidityMonths, setInspectionValidityMonths] = useState(60);
  const [openInspectionValidityDialog, setOpenInspectionValidityDialog] = useState(false);
  const [savingInspectionValidity, setSavingInspectionValidity] = useState(false);
  const [locationSort, setLocationSort] = useState("count");
  const theme = useTheme();
  const usesDualScroll = useMediaQuery(theme.breakpoints.up('lg')); // desktop: split panes with independent scroll
  // Memoized canManageUsers that safely compares IDs as strings and recomputes when me or assignedUsers change
  const canManageUsers = React.useMemo(() => {
    if (!me) return false;
    if (me.role === 'admin') return true;
    if (me.is_db_supervisor) return true;
    const meId = me?.id != null ? String(me.id) : null;
    const supervisorInAssigned = assignedUsers?.some(u => String(u.id) === meId && !!u.is_db_supervisor);
    return !!supervisorInAssigned;
  }, [me, assignedUsers]);
  const canManageInspectionValidity = me?.role === 'admin' || me?.is_db_supervisor === true;
  const navigate = useNavigate();
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [openClassificationDialog, setOpenClassificationDialog] =
    useState(false);
  const [classificationState, setClassificationState] = useState(EMPTY_CLASSIFICATION_STATE);
  useEffect(() => {
    let isMounted = true;
    async function fetchClassificationSettings() {
      try {
        const token = localStorage.getItem("token");
        const res = await api.get("/api/settings/classification", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (isMounted && res.data && res.data.length > 0) {
          const row = res.data[0] || EMPTY_CLASSIFICATION_STATE;
          setClassificationState(normalizeClassificationState(row));
        }
      } catch (err) {
        console.error("Error fetching classification settings:", err);
      }
    }
    async function fetchStatistics() {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const statsRes = await api.get("/api/equipment/statistics", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const typePercentRes = await api.get(
          "/api/equipment/statistics/inspection-type-percentages",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (isMounted) {
          setStats(statsRes.data);
          setTypePercentages(typePercentRes.data);
          setInspectionValidityMonths(statsRes.data?.inspectionWindow?.months ?? 60);
          setError(null);
        }
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        if (isMounted) {
          setError("Failed to load dashboard data. Please contact admin.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    const fetchExpiryDate = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/settings/expiry`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        console.log("Expiry API Response:", response.data);
        setExpiryDate(response.data.expiryDate || null);
        setExpiryIsExpired(Boolean(response.data.isExpired));
      } catch (error) {
        console.error("Error fetching expiry date:", error);
      }
    };
    async function fetchPlanDetails() {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/settings/plan-details`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (isMounted) {
          setPlanType(response.data.planType);
          setCurrentUsers(response.data.currentUsers);
          setUserLimit(response.data.userLimit);
          setItemLimit(response.data.itemLimit);
        }
      } catch (err) {
        console.error("Error fetching plan details:", err);
      }
    }
    async function fetchMe() {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/me`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (isMounted) setMe(res.data);
      } catch (err) {
        console.error('Error fetching current user (me):', err);
      }
    }
    // --- Added: fetch assigned users once on mount to enable immediate flashing ---
    async function fetchAssignedUsersInitial() {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/users/assigned`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (isMounted) {
          setAssignedUsers(res.data);
          setMe(prev => {
            if (!prev) return prev;
            const mine = res.data.find(u => String(u.id) === String(prev.id));
            if (mine?.is_db_supervisor === true && prev.is_db_supervisor !== true) {
              return { ...prev, is_db_supervisor: true };
            }
            return prev;
          });
        }
      } catch (e) {
        // silent on mount to avoid toast noise
        console.warn('Initial assigned users fetch failed:', e?.response?.data || e?.message);
      }
    }
    // --- End added ---
    fetchClassificationSettings();
    fetchExpiryDate();
    fetchStatistics();
    fetchPlanDetails();
    fetchMe();
    fetchAssignedUsersInitial();
    // Fetch dbCode
    async function fetchDbCode() {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/users/db-code`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (isMounted) setDbCode(res.data?.db_code || null);
      } catch (e) {
        console.warn('Failed to fetch db_code:', e?.response?.data || e?.message);
      }
    }
    fetchDbCode();
    return () => {
      isMounted = false;
    };
  }, []);
  const refreshStatistics = async () => {
    let isMounted = true;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const statsRes = await api.get("/api/equipment/statistics", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const typePercentRes = await api.get(
        "/api/equipment/statistics/inspection-type-percentages",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (isMounted) {
        setStats(statsRes.data);
        setTypePercentages(typePercentRes.data);
        setInspectionValidityMonths(statsRes.data?.inspectionWindow?.months ?? 60);
      }
    } catch (err) {
      console.error("Error refreshing stats:", err);
      toast.error("Failed to refresh statistics.");
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
    return () => {
      isMounted = false;
    };
  };
  const handleClassificationCheckboxChange = (group, standard) => {
    setClassificationState((prev) => {
      const next = toggleGroupStandard(prev, group, standard);
      if (next === prev) {
        toast.warning("Gas/Vapor and Dust cannot use different standards.");
      }
      return next;
    });
  };
  const handleSaveClassification = async () => {
    try {
      const token = localStorage.getItem("token");
      await api.post("/api/settings/classification", classificationState, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpenClassificationDialog(false);
      refreshStatistics();
    } catch (err) {
      console.error("Error updating classification:", err);
      toast.error("Failed to update classification setting.");
    }
  };
  const handleSaveInspectionValidity = async () => {
    if (!canManageInspectionValidity) return;
    try {
      setSavingInspectionValidity(true);
      const token = localStorage.getItem("token");
      const res = await api.put(
        "/api/settings/inspection-validity",
        { inspectionValidityMonths },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setInspectionValidityMonths(res.data?.inspectionValidityMonths ?? inspectionValidityMonths);
      setOpenInspectionValidityDialog(false);
      await refreshStatistics();
      toast.success("Inspection validity updated.");
    } catch (err) {
      console.error("Error updating inspection validity:", err);
      const message =
        err?.response?.data?.message || "Failed to update inspection validity.";
      toast.error(message);
    } finally {
      setSavingInspectionValidity(false);
    }
  };
  const zoneDescriptions = {
    gas_vapor_iec: {
      "Zone 0":
        "Explosive atmosphere is present continuously, or for long periods.",
      "Zone 1":
        "Explosive atmosphere is likely to occur in normal operation occasionally.",
      "Zone 2":
        "Explosive atmosphere is not likely to occur in normal operation, and if it does, it is short-lived.",
    },
    gas_vapor_atex: {
      "Category 1G":
        "Explosive atmosphere is present continuously, or for long periods.",
      "Category 2G":
        "Explosive atmosphere is likely to occur in normal operation occasionally.",
      "Category 3G":
        "Explosive atmosphere is not likely to occur in normal operation.",
    },
    gas_vapor_nec500: {
      "Class I Division 1":
        "Hazardous material present continuously, intermittently, or periodically under normal conditions.",
      "Class I Division 2":
        "Hazardous material present only under abnormal conditions, for a short period of time.",
    },
    dust_iec: {
      "Zone 20":
        "Combustible dust is present continuously or for long periods.",
      "Zone 21":
        "Combustible dust is likely to occur in normal operation occasionally.",
      "Zone 22":
        "Combustible dust is not likely to occur in normal operation, and if it does, it is short-lived.",
    },
    dust_atex: {
      "Category 1D": "Dust cloud present continuously, or for long periods.",
      "Category 2D":
        "Dust cloud likely to occur in normal operation occasionally.",
      "Category 3D":
        "Dust cloud not likely in normal operation, but if it occurs, it is short-lived.",
    },
    dust_nec500: {
      "Class II Division 1":
        "Combustible dust present continuously, intermittently, or periodically under normal conditions.",
      "Class II Division 2":
        "Combustible dust present only under abnormal conditions, for a short period of time.",
    },
  };
  const DEFAULT_DESCRIPTIONS = {
    "Non-Hazardous": "Equipment located in a non-hazardous area.",
    Undefined:
      "Items without a defined hazardous zone defined or wrong classification.",
  };
  if (loading) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }
  if (!stats) {
    return null;
  }
  const {
    totalItems,
    failCount,
    passCount,
    uninspectedCount,
    recentInspectionPercentage,
    zoneCounts,
    areaCounts,
    monthlyInspections,
    inspectionWindow,
    inspectionDue,
  } = stats;
  const inspectionWindowMonths = inspectionWindow?.months || inspectionValidityMonths || 60;
  const inspectionWindowYears = inspectionWindow?.years || inspectionWindowMonths / 12;
  const inspectionDueSummary = inspectionDue || {
    overdueCount: 0,
    dueIn30Count: 0,
    dueIn31To60Count: 0,
  };
  const sortedAreaCounts = (areaCounts || []).slice().sort((a, b) => {
    if (locationSort === "alpha") {
      return (a?.name || "").localeCompare(b?.name || "");
    }
    return (b?.count || 0) - (a?.count || 0);
  });
  const {
    totalInspected,
    visualCount,
    closeCount,
    detailedCount,
    percentageVisual,
    percentageClose,
    percentageDetailed,
  } = typePercentages || {};
  const last60Months = Array.from({ length: 60 }, (_, i) =>
    dayjs()
      .subtract(59 - i, "month")
      .format("YYYY-MM")
  );
  const monthlyData = last60Months.map((month) => {
    const found = monthlyInspections.find((m) => m.month === month);
    return found ? found.count : 0;
  });
  function getZoneData(zoneName) {
    const found = zoneCounts.find((z) => z.zone === zoneName);
    return found
      ? {
          total: found.total,
          pass: found.pass,
          fail: found.fail,
          missingInspection: found.missingInspection,
        }
      : { total: 0, fail: 0, pass: 0, missingInspection: 0 };
  }
  let classificationRows = [];
  if (classificationState.gas_vapor_iec) {
    classificationRows = [
      ...classificationRows,
      {
        id: 1,
        zone: "Zone 0",
        description:
          zoneDescriptions.gas_vapor_iec["Zone 0"] ??
          DEFAULT_DESCRIPTIONS["Zone 0"],
        ...getZoneData("Zone 0"),
      },
      {
        id: 2,
        zone: "Zone 1",
        description:
          zoneDescriptions.gas_vapor_iec["Zone 1"] ??
          DEFAULT_DESCRIPTIONS["Zone 1"],
        ...getZoneData("Zone 1"),
      },
      {
        id: 3,
        zone: "Zone 2",
        description:
          zoneDescriptions.gas_vapor_iec["Zone 2"] ??
          DEFAULT_DESCRIPTIONS["Zone 2"],
        ...getZoneData("Zone 2"),
      },
    ];
  }
  if (classificationState.gas_vapor_atex) {
    classificationRows = [
      ...classificationRows,
      {
        id: 4,
        zone: "Category 1G",
        description:
          zoneDescriptions.gas_vapor_atex["Category 1G"] ??
          DEFAULT_DESCRIPTIONS["Category 1G"],
        ...getZoneData("Category 1G"),
      },
      {
        id: 5,
        zone: "Category 2G",
        description:
          zoneDescriptions.gas_vapor_atex["Category 2G"] ??
          DEFAULT_DESCRIPTIONS["Category 2G"],
        ...getZoneData("Category 2G"),
      },
      {
        id: 6,
        zone: "Category 3G",
        description:
          zoneDescriptions.gas_vapor_atex["Category 3G"] ??
          DEFAULT_DESCRIPTIONS["Category 3G"],
        ...getZoneData("Category 3G"),
      },
    ];
  }
  if (classificationState.gas_vapor_nec500) {
    classificationRows = [
      ...classificationRows,
      {
        id: 7,
        zone: "Class I Division 1",
        description:
          zoneDescriptions.gas_vapor_nec500["Class I Division 1"] ??
          DEFAULT_DESCRIPTIONS["Class I Division 1"],
        ...getZoneData("Class I Division 1"),
      },
      {
        id: 8,
        zone: "Class I Division 2",
        description:
          zoneDescriptions.gas_vapor_nec500["Class I Division 2"] ??
          DEFAULT_DESCRIPTIONS["Class I Division 2"],
        ...getZoneData("Class I Division 2"),
      },
    ];
  }
  if (classificationState.dust_iec) {
    classificationRows = [
      ...classificationRows,
      {
        id: 9,
        zone: "Zone 20",
        description:
          zoneDescriptions.dust_iec["Zone 20"] ??
          DEFAULT_DESCRIPTIONS["Zone 20"],
        ...getZoneData("Zone 20"),
      },
      {
        id: 10,
        zone: "Zone 21",
        description:
          zoneDescriptions.dust_iec["Zone 21"] ??
          DEFAULT_DESCRIPTIONS["Zone 21"],
        ...getZoneData("Zone 21"),
      },
      {
        id: 11,
        zone: "Zone 22",
        description:
          zoneDescriptions.dust_iec["Zone 22"] ??
          DEFAULT_DESCRIPTIONS["Zone 22"],
        ...getZoneData("Zone 22"),
      },
    ];
  }
  if (classificationState.dust_atex) {
    classificationRows = [
      ...classificationRows,
      {
        id: 12,
        zone: "Category 1D",
        description:
          zoneDescriptions.dust_atex["Category 1D"] ??
          DEFAULT_DESCRIPTIONS["Category 1D"],
        ...getZoneData("Category 1D"),
      },
      {
        id: 13,
        zone: "Category 2D",
        description:
          zoneDescriptions.dust_atex["Category 2D"] ??
          DEFAULT_DESCRIPTIONS["Category 2D"],
        ...getZoneData("Category 2D"),
      },
      {
        id: 14,
        zone: "Category 3D",
        description:
          zoneDescriptions.dust_atex["Category 3D"] ??
          DEFAULT_DESCRIPTIONS["Category 3D"],
        ...getZoneData("Category 3D"),
      },
    ];
  }
  if (classificationState.dust_nec500) {
    classificationRows = [
      ...classificationRows,
      {
        id: 15,
        zone: "Class II Division 1",
        description:
          zoneDescriptions.dust_nec500["Class II Division 1"] ??
          DEFAULT_DESCRIPTIONS["Class II Division 1"],
        ...getZoneData("Class II Division 1"),
      },
      {
        id: 16,
        zone: "Class II Division 2",
        description:
          zoneDescriptions.dust_nec500["Class II Division 2"] ??
          DEFAULT_DESCRIPTIONS["Class II Division 2"],
        ...getZoneData("Class II Division 2"),
      },
    ];
  }
  classificationRows.push(
    {
      id: 999,
      zone: "Non-Hazardous",
      description: DEFAULT_DESCRIPTIONS["Non-Hazardous"],
      ...getZoneData("Non-Hazardous"),
    },
    {
      id: 1000,
      zone: "Undefined",
      description: DEFAULT_DESCRIPTIONS["Undefined"],
      ...getZoneData("Undefined"),
    }
  );
  const isExpired = expiryIsExpired;
  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        px: { xs: 1, sm: 2 },
        pt: 1,
        pb: { xs: 2, lg: 2 },
        overflowX: 'hidden',
        overflowY: usesDualScroll ? 'hidden' : 'auto',
        height: usesDualScroll ? `calc(100vh - ${NAVBAR_HEIGHT_PX}px)` : 'auto',
        minHeight: `calc(100vh - ${NAVBAR_HEIGHT_PX}px)`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isBranded ? (
        <Box className="branded-workspace-heading">
          <Typography component="h1" className="branded-workspace-eyebrow">{brand.name} asset overview</Typography>
        </Box>
      ) : (
      <Typography
        component="h1"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          p: 0,
          m: -1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        Dashboard overview
      </Typography>
      )}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1, sm: 1.5 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
          flexShrink: 0,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.25}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          {/* Left: actions */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<PrintIcon fontSize="small" />}
              onClick={() => setOpenReportDialog(true)}
              sx={{
                ...dashboardActionButtonSx,
                backgroundColor: isBranded ? "primary.main" : "#0f172a",
                color: "#fff",
                boxShadow: "0 8px 18px rgba(15, 23, 42, 0.16)",
                "&:hover": {
                  backgroundColor: isBranded ? "primary.dark" : "#111827",
                  boxShadow: "0 10px 22px rgba(15, 23, 42, 0.2)",
                },
              }}
            >
              Generate report
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FactCheckOutlinedIcon fontSize="small" />}
              onClick={() => setOpenClassificationDialog(true)}
              sx={dashboardActionButtonSx}
            >
              Classification
            </Button>
            {canManageInspectionValidity && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ScheduleOutlinedIcon fontSize="small" />}
                onClick={() => setOpenInspectionValidityDialog(true)}
                sx={dashboardActionButtonSx}
              >
                Inspection cycle
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<PeopleAltOutlinedIcon fontSize="small" />}
              onClick={() => {
                setOpenUserDialog(true);
              }}
              sx={{
                ...dashboardActionButtonSx,
                ...(me && me.role === "viewer" && canManageUsers
                  ? {
                      position: "relative",
                      borderColor: "error.main",
                      color: "error.main",
                      backgroundColor: "rgba(254, 242, 242, 0.96)",
                      "@keyframes subtleFlash": {
                        "0%": {
                          backgroundColor: "rgba(254, 242, 242, 0.96)",
                          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
                        },
                        "50%": {
                          backgroundColor: "rgba(254, 226, 226, 1)",
                          boxShadow: "0 8px 18px rgba(239, 68, 68, 0.14)",
                        },
                        "100%": {
                          backgroundColor: "rgba(254, 242, 242, 0.96)",
                          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
                        },
                      },
                      animation: "subtleFlash 1.4s ease-in-out infinite",
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                      },
                    }
                  : {}),
              }}
            >
              Users ({currentUsers} / {userLimit})
            </Button>
            <RfidStatisticsDialog actionSx={dashboardActionButtonSx} />
            <InspectionQuestionsDialog actionSx={dashboardActionButtonSx} />
          </Stack>

          {/* Center: current date */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{ flex: 1 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {dayjs().format('dddd, DD MMM YYYY')}
            </Typography>
          </Stack>

          {/* Right: plan + expiry */}
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            flexWrap="wrap"
            sx={{
              width: { xs: "100%", md: "auto" },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              Plan:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
              {planType ? `${planType} plan` : "Not set"}
            </Typography>
            {expiryDate && (
              <>
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    backgroundColor: "text.disabled",
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  Expires:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: isExpired ? "error.main" : "text.primary" }}
                >
                  {dayjs(expiryDate).format("DD MMM YYYY")}
                </Typography>
              </>
            )}
          </Stack>
        </Stack>
      </Paper>
      {/* Main content: 2-column on lg (Locations spans full height). Stacks on small screens. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mt: 1.5,
          gap: 1.5,
          flex: 1,
          minHeight: 0,
          overflow: usesDualScroll ? 'hidden' : 'visible',
        }}
      >
        <Grid
          container
          spacing={1.5}
          sx={{
            flex: 1,
            minHeight: 0,
            height: { xs: 'auto', lg: '100%' },
            alignItems: 'stretch',
          }}
        >
          {/* LEFT: KPIs + Zone overview + Chart */}
          <Grid
            item
            xs={12}
            lg={8}
            sx={{
              minHeight: 0,
              height: { xs: 'auto', lg: '100%' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                height: { xs: 'auto', lg: '100%' },
                flex: 1,
                minHeight: 0,
                p: { xs: 0.75, sm: 1 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  minHeight: 0,
                  overflowY: usesDualScroll ? 'auto' : 'visible',
                  pr: { xs: 0, lg: 0.5 },
                }}
              >
              {/* KPI grid (3 columns on desktop) */}
              <Grid container spacing={1.5}>
                {/* TOTAL ITEMS */}
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardKpiCard
                    label="TOTAL ITEMS"
                    value={totalItems}
                    secondaryValue={`/ ${itemLimit}`}
                    tone="neutral"
                    progressValue={itemLimit > 0 ? (totalItems / itemLimit) * 100 : undefined}
                    progressLabel="Equipment register capacity used"
                  />
                </Grid>

                {/* PASSED */}
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardKpiCard label="PASSED" value={passCount} tone="success" />
                </Grid>

                {/* NOT INSPECTED */}
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardKpiCard
                    label="NOT INSPECTED"
                    value={uninspectedCount}
                    tone="warning"
                    onActivate={() => navigate('/exregister', { state: { filter: 'uninspected' } })}
                    ariaLabel={`View ${uninspectedCount} not inspected items`}
                  />
                </Grid>

                {/* FAILED */}
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardKpiCard
                    label="FAILED"
                    value={failCount}
                    tone="error"
                    onActivate={() => navigate('/exregister', { state: { filter: 'failed' } })}
                    ariaLabel={`View ${failCount} failed items`}
                  />
                </Grid>

                {/* OVERDUE */}
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardKpiCard
                    label={`OVERDUE (${inspectionWindowYears}Y+)`}
                    value={inspectionDueSummary.overdueCount}
                    tone="error"
                    supportingText={`Based on ${formatInspectionValidityLabel(inspectionWindowMonths)} inspection cycle`}
                    onActivate={() =>
                      navigate('/exregister', {
                        state: {
                          filter: 'inspectionDue',
                          bucket: 'overdue',
                          months: inspectionWindowMonths,
                        },
                      })
                    }
                    ariaLabel={`View ${inspectionDueSummary.overdueCount} overdue items`}
                  />
                </Grid>

                {/* DUE IN 30 DAYS */}
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardKpiCard
                    label="DUE IN 30 DAYS"
                    value={inspectionDueSummary.dueIn30Count}
                    tone="warning"
                    supportingText="Inspection due in the next 30 days"
                    onActivate={() =>
                      navigate('/exregister', {
                        state: {
                          filter: 'inspectionDue',
                          bucket: 'due30',
                          months: inspectionWindowMonths,
                        },
                      })
                    }
                    ariaLabel={`View ${inspectionDueSummary.dueIn30Count} items due within 30 days`}
                  />
                </Grid>

                {/* DUE IN 31-60 DAYS */}
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardKpiCard
                    label="DUE IN 31–60 DAYS"
                    value={inspectionDueSummary.dueIn31To60Count}
                    tone="info"
                    supportingText="Inspection due in 31 to 60 days"
                    onActivate={() =>
                      navigate('/exregister', {
                        state: {
                          filter: 'inspectionDue',
                          bucket: 'due60',
                          months: inspectionWindowMonths,
                        },
                      })
                    }
                    ariaLabel={`View ${inspectionDueSummary.dueIn31To60Count} items due within 31 to 60 days`}
                  />
                </Grid>

                {/* INSPECTION COVERAGE */}
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardKpiCard
                    label="INSPECTION COVERAGE"
                    value={`${formatDashboardPercent(recentInspectionPercentage)}%`}
                    tone="primary"
                    supportingText="Calculated over 5-year period"
                    progressValue={recentInspectionPercentage}
                    progressLabel="Inspection coverage"
                  />
                </Grid>

                {/* VISUAL */}
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardKpiCard
                    label="VISUAL"
                    value={`${formatDashboardPercent(percentageVisual)}%`}
                    tone="primary"
                    supportingText={`${visualCount} / ${totalInspected} inspected`}
                    progressValue={percentageVisual}
                    progressLabel="Visual inspections"
                  />
                </Grid>

                {/* CLOSE */}
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardKpiCard
                    label="CLOSE"
                    value={`${formatDashboardPercent(percentageClose)}%`}
                    tone="primary"
                    supportingText={`${closeCount} / ${totalInspected} inspected`}
                    progressValue={percentageClose}
                    progressLabel="Close inspections"
                  />
                </Grid>

                {/* DETAILED */}
                <Grid item xs={12} sm={6} md={4}>
                  <DashboardKpiCard
                    label="DETAILED"
                    value={`${formatDashboardPercent(percentageDetailed)}%`}
                    tone="primary"
                    supportingText={`${detailedCount} / ${totalInspected} inspected`}
                    progressValue={percentageDetailed}
                    progressLabel="Detailed inspections"
                  />
                </Grid>
              </Grid>

              {/* Zone overview */}
              <Paper
                elevation={0}
                sx={{
                  flex: '0 0 auto',
                  minHeight: 260,
                  p: 1,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                  Zone overview
                </Typography>
                <Box sx={{ minHeight: 0 }}>
                  <DataGridPro
                    rows={classificationRows}
                    columns={[
                      { field: 'zone', headerName: 'Zone', flex: 1, headerAlign: 'center', align: 'center', minWidth: 120 },
                      { field: 'description', headerName: 'Description', flex: 3, headerAlign: 'center', minWidth: 280 },
                      { field: 'total', headerName: 'Total', flex: 1, headerAlign: 'center', minWidth: 90, align: 'center' },
                      { field: 'pass', headerName: 'Passed', flex: 1, headerAlign: 'center', minWidth: 95, align: 'center', headerClassName: 'passed-header', cellClassName: 'passed-cell' },
                      { field: 'fail', headerName: 'Failed', flex: 1, headerAlign: 'center', minWidth: 95, align: 'center', headerClassName: 'failed-header', cellClassName: 'failed-cell' },
                      { field: 'missingInspection', headerName: 'Missing', flex: 1, headerAlign: 'center', minWidth: 105, align: 'center', headerClassName: 'missing-header', cellClassName: 'missing-cell' },
                    ]}
                    getRowHeight={(params) => (params.row?.isDivider ? 5 : 36)}
                    disableColumnMenu
                    disableSelectionOnClick
                    hideFooter
                    autoHeight
                    sx={{
                      fontFamily:
                        "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      fontSize: 15,
                      '& .MuiDataGrid-columnHeaders': { fontSize: 13 },
                      '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 800 },
                      backgroundColor: '#fff',
                      '& .passed-header': { color: 'success.dark' },
                      '& .failed-header': { color: 'error.dark' },
                      '& .missing-header': { color: 'warning.dark' },
                      '& .passed-cell': { color: 'success.dark', fontWeight: 700 },
                      '& .failed-cell': { color: 'error.dark', fontWeight: 700 },
                      '& .missing-cell': { color: 'warning.dark', fontWeight: 700 },
                      '& .MuiDataGrid-root': { backgroundColor: 'white', borderRadius: 2, boxShadow: 0 },
                      "& .MuiDataGrid-row[data-rowindex='999'], & .MuiDataGrid-row[data-rowindex='998']": {
                        backgroundColor: 'transparent !important',
                        height: '5px !important',
                        minHeight: '5px !important',
                      },
                      "& .MuiDataGrid-row[data-rowindex='999'] .MuiDataGrid-cell, & .MuiDataGrid-row[data-rowindex='998'] .MuiDataGrid-cell": {
                        border: 'none !important',
                        padding: '0px !important',
                        fontSize: '2px !important',
                      },
                    }}
                  />
                </Box>
              </Paper>

              {/* Inspections per month (kept in left column) */}
              <Paper
                elevation={0}
                sx={{
                  flex: '0 0 auto',
                  p: 1,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: '#fff',
                }}
              >
                <Box sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Monthly inspection activity
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last 60 months
                  </Typography>
                </Box>
                <ReactApexChart
                  options={{
                    chart: { type: 'bar', toolbar: { show: false }, foreColor: '#5f6c7b' },
                    grid: { borderColor: '#e7ecf1', strokeDashArray: 3 },
                    xaxis: {
                      categories: last60Months,
                      tickAmount: 12,
                      labels: { rotate: -35, hideOverlappingLabels: true, trim: true },
                      axisBorder: { color: '#dbe2ea' },
                      axisTicks: { color: '#dbe2ea' },
                    },
                    yaxis: { title: { text: 'Inspections' }, min: 0 },
                    plotOptions: { bar: { borderRadius: 3, columnWidth: '72%', horizontal: false } },
                    colors: [isBranded ? brand.primary : '#1f6fb2'],
                    dataLabels: { enabled: false },
                    tooltip: { enabled: true },
                  }}
                  series={[{ name: 'Inspections', data: monthlyData }]}
                  type="bar"
                  height={usesDualScroll ? 260 : 220}
                />
              </Paper>
            </Box>
          </Paper>
        </Grid>

          {/* RIGHT: Locations list spans full height on lg, scroll inside only */}
          <Grid
            item
            xs={12}
            lg={4}
            sx={{
              minHeight: 0,
              height: { xs: 'auto', lg: '100%' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                height: { xs: 'auto', lg: '100%' },
                flex: { xs: 'unset', lg: 1 },
                minHeight: 0,
                p: 1,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Locations
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant={locationSort === "alpha" ? "contained" : "outlined"}
                    onClick={() => setLocationSort("alpha")}
                  >
                    A-Z
                  </Button>
                  <Button
                    size="small"
                    variant={locationSort === "count" ? "contained" : "outlined"}
                    onClick={() => setLocationSort("count")}
                  >
                    # Items
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
                <Stack spacing={0.5}>
                  {sortedAreaCounts.map((a) => (
                      <Button
                        key={a.name}
                        variant="text"
                        onClick={() => navigate('/exregister', { state: { filter: 'area', area: a.name } })}
                        sx={{
                          justifyContent: 'space-between',
                          textTransform: 'none',
                          borderRadius: 2,
                          px: 1,
                          py: 0.75,
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(6px)',
                          WebkitBackdropFilter: 'blur(6px)',
                          '&:hover': { boxShadow: 1 },
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>{a.name}</span>
                        <Chip size="small" label={a.count ?? 0} />
                      </Button>
                    ))}

                  {(!areaCounts || areaCounts.length === 0) && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      No locations found.
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      <ReportDialog open={openReportDialog} onClose={() => setOpenReportDialog(false)} areas={sortedAreaCounts} />
      <RegisterUsersDialog open={openUserDialog} onClose={() => setOpenUserDialog(false)} me={me} dbCode={dbCode}
        onUsersChange={users => {
          setAssignedUsers(users);
          setMe(previous => {
            if (!previous) return previous;
            const mine = users.find(user => String(user.id) === String(previous.id));
            return mine ? { ...previous, role: mine.role, is_db_supervisor: mine.is_db_supervisor } : previous;
          });
        }}
        onUsage={usage => { setCurrentUsers(usage.currentUsers); setUserLimit(usage.userLimit); }} />
      <Dialog
        open={canManageInspectionValidity && openInspectionValidityDialog}
        onClose={() => setOpenInspectionValidityDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ backgroundColor: "#f0f2f5", fontWeight: "bold" }}>
          Inspection Validity Period
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#fafafa", pb: 2 }}>
          <Box
            sx={{
              mt: 1,
              p: 2,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <FormLabel sx={{ fontWeight: "bold", color: "text.primary", mb: 1, display: "block" }}>
              Select inspection cycle
            </FormLabel>
            <RadioGroup
              value={String(inspectionValidityMonths)}
              onChange={(event) => setInspectionValidityMonths(Number(event.target.value))}
            >
              {INSPECTION_VALIDITY_OPTIONS.map((months) => (
                <FormControlLabel
                  key={months}
                  value={String(months)}
                  control={<Radio />}
                  label={`${formatInspectionValidityLabel(months)} (${months} months)`}
                />
              ))}
            </RadioGroup>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
              This controls the Overdue, Due in 30 days, and Due in 31-60 days dashboard cards.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenInspectionValidityDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveInspectionValidity}
            disabled={savingInspectionValidity}
          >
            {savingInspectionValidity ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openClassificationDialog}
        onClose={() => setOpenClassificationDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ backgroundColor: "#f0f2f5", fontWeight: "bold" }}>
          Classification Settings
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#fafafa", pb: 2 }}>
          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
              Gas / Vapor
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={classificationState.gas_vapor_iec}
                    disabled={getCrossStandardDisabled(classificationState, "gas", "iec")}
                    onChange={() =>
                      handleClassificationCheckboxChange("gas", "iec")
                    }
                  />
                }
                label="IEC"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={classificationState.gas_vapor_atex}
                    disabled={getCrossStandardDisabled(classificationState, "gas", "atex")}
                    onChange={() =>
                      handleClassificationCheckboxChange("gas", "atex")
                    }
                  />
                }
                label="ATEX"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={classificationState.gas_vapor_nec500}
                    disabled={getCrossStandardDisabled(classificationState, "gas", "nec500")}
                    onChange={() =>
                      handleClassificationCheckboxChange("gas", "nec500")
                    }
                  />
                }
                label="NEC500"
              />
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              p: 2,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
              Dust
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={classificationState.dust_iec}
                    disabled={getCrossStandardDisabled(classificationState, "dust", "iec")}
                    onChange={() =>
                      handleClassificationCheckboxChange("dust", "iec")
                    }
                  />
                }
                label="IEC"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={classificationState.dust_atex}
                    disabled={getCrossStandardDisabled(classificationState, "dust", "atex")}
                    onChange={() =>
                      handleClassificationCheckboxChange("dust", "atex")
                    }
                  />
                }
                label="ATEX"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={classificationState.dust_nec500}
                    disabled={getCrossStandardDisabled(classificationState, "dust", "nec500")}
                    onChange={() =>
                      handleClassificationCheckboxChange("dust", "nec500")
                    }
                  />
                }
                label="NEC500"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenClassificationDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveClassification}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
export default Dashboard;
